import { AbstractControl } from "@angular/forms";
import { 
  validatePostalCode,
  isValueWholeNumber,
  validateYearFormat,
  validateDateFormat,
  isDateInThePast,
  validatePhoneNumber,
  validateEmailAddress,
  validateRiskUnitsNumber,
  validateDateWithinRange,
  validateDateWithinRangeOfDate,
  requiredByValues,
  validateDateNotAfter,
  requiredByValuesExceptWhen,
  requiredByRule,
  enabledByValues,
  requiredWhenNotEmpty,
  requiredByOperation,
  validateName
} from "collabor8-funcs";

export const isValueWholeNumberWrapper = (control: AbstractControl): { [key: string]: any } | null => {
  return isValueWholeNumber(control.value);
}

export const validateYearFormatWrapper = (control: AbstractControl): { [key: string]: any } | null => {
  return validateYearFormat(control.value);
}

export const validatePostalCodeWrapper = (control: AbstractControl): { [key: string]: any } | null => {
  return validatePostalCode(control.value);
}

export const validateDateFormatWrapper = (control: AbstractControl): { [key: string]: any } | null => {
  return validateDateFormat(control.value);
}

export const isDateInThePastWrapper = (control: AbstractControl): { [key: string]: any } | null => {
  return isDateInThePast(control.value);
}

export const validatePhoneNumberWrapper = (control: AbstractControl): { [key: string]: any } | null => {
  return validatePhoneNumber(control.value);
}

export const validateEmailAddressWrapper = (control: AbstractControl): { [key: string]: any } | null => {
  return validateEmailAddress(control.value);
}

export const validateRiskUnitsNumberWrapper = (control: AbstractControl): { [key: string]: any } | null => {
  return validateRiskUnitsNumber(control.value);
}

export const validateDateWithinRangeWrapper = (min, max) => {
  return (control: AbstractControl): { [key: string]: any } | null => {
    return validateDateWithinRange(control.value, min, max);
  }
}

export const validateDateWithinRangeOfDateWrapper = (field, min, max) => {
  return (control: AbstractControl): { [key: string]: any } | null => {
    return validateDateWithinRangeOfDate(control.value, field.value, min, max);
  }
}

export const requiredByValuesWrapper = (field, requiredValues, invert = false) => {
  let subscriptionCreated = false;
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!subscriptionCreated && field.valueChanges) {
      subscriptionCreated = true;
      field.valueChanges.subscribe(() => {
        control.updateValueAndValidity({ emitEvent: false });
      });
    }
    return requiredByValues(control.value, field.value, requiredValues, invert);
  }
}

export const validateDateNotAfterWrapper = (field, label?) => {
  return (control: AbstractControl): { [key: string]: any } | null => {
    return validateDateNotAfter(control.value, field?.value, label);
  }
}

export const requiredByValuesExceptWhenWrapper = (field, requiredValues, exceptField, exceptValues) => {
  let subscriptionCreated = false;
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!subscriptionCreated) {
      subscriptionCreated = true;
      [field, exceptField].forEach(source => source?.valueChanges?.subscribe(() => {
        control.updateValueAndValidity({ emitEvent: false });
      }));
    }
    return requiredByValuesExceptWhen(control.value, field.value, requiredValues, exceptField?.value, exceptValues);
  }
}

export const requiredByRuleWrapper = (getIsRuleActive: () => boolean, invert = false) => {
  return (control: AbstractControl): { [key: string]: any } | null => {
    return requiredByRule(control.value, getIsRuleActive(), invert);
  }
}

export const requiredWhenNotEmptyWrapper = (field, invert = false) => {
  let subscriptionCreated = false;
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!subscriptionCreated && field.valueChanges) {
      subscriptionCreated = true;
      field.valueChanges.subscribe(() => {
        control.updateValueAndValidity({ emitEvent: false });
      });
    }
    return requiredWhenNotEmpty(control.value, field.value, invert);
  }
}

export const requiredByOperationWrapper = (field, requiredValue: number, op) => {
  let subscriptionCreated = false;
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!subscriptionCreated && field.valueChanges) {
      subscriptionCreated = true;
      field.valueChanges.subscribe(() => {
        control.updateValueAndValidity({ emitEvent: false });
      });
    }
    return requiredByOperation(control.value, field.value, requiredValue, op);
  }
}

export const validateNameWrapper = (control: AbstractControl): { [key: string]: any } | null => {
  return validateName(control.value);
}

export const enabledByValuesWrapper = (field, requiredValues, numOfInstances, invert = false) => {
  const applyEnablingState = (control: AbstractControl) => {
    let shouldEnable = false;
    control["_enableByValuesCond"].forEach(({source, requiredValues, invert}) => {
      shouldEnable = shouldEnable || enabledByValues(source.value, requiredValues, invert);
    })

    if (shouldEnable && control.disabled) {
      control.enable({ emitEvent: false });
      control.updateValueAndValidity({ emitEvent: false });
    } else if (!shouldEnable) {
      if (control.enabled) { 
        control.disable({ emitEvent: false }); 
        control.setValue(null, { emitEvent: false }); 
      }
      control.setErrors(null);
    }
  };

  return (control: AbstractControl) => {
    if (!control["_enableByValuesCond"]) {
      control["_enableByValuesCond"] = [];
    }
    if (!control["_enableByValuesCond"].find(c => c.source == field)) {
      control["_enableByValuesCond"].push({ source: field, requiredValues: requiredValues, invert: invert });
    }
    
    if (control["_enableByValuesCond"].length == numOfInstances) {
      for (const enableCond of control["_enableByValuesCond"]) {
        (['setValue', 'patchValue'] as const).forEach(method => {
          const original = enableCond.source[method].bind(enableCond.source);
          enableCond.source[method] = (...args: any[]) => { original(...args); applyEnablingState(control); };
        });
        enableCond.source.valueChanges.subscribe(() => applyEnablingState(control));
      }
      applyEnablingState(control);
    }
  };
}